import { useState } from 'react';

import { Box, Button, ButtonGroup, Modal, TextButton, Toast, useToast } from '@zillow/constellation-10';

import { SmartConfigMode } from '@zg-rentals/ts-rental-manager-api-client';
import { useRentalManagerUserPropertySpotlightWebApiHandler_setSmartConfigMode } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { css } from '@/styled-system/css';

import { optionCardRecipe, optionTagRecipe, radioMarkRecipe } from './SmartAssignActivationModal.styles';

import { SPOTLIGHT_COPY as COPY } from './spotlightCopy';

const MODAL_COPY = COPY.smartAssign.modal;

const strongNeutral = css({ color: '{colors.text.neutral}' });

function OptionCard({
  selected,
  tag,
  title,
  desc,
  note,
  onSelect,
  testId,
}: {
  selected: boolean;
  tag: string;
  title: string;
  desc: string;
  note: string;
  onSelect: () => void;
  testId: string;
}) {
  return (
    <label className={optionCardRecipe({ selected })}>
      <input
        type="radio"
        name="smart-assign-mode"
        checked={selected}
        onChange={onSelect}
        aria-label={title}
        data-testid={testId}
        className={css({ position: 'absolute', width: '1px', height: '1px', margin: 0, opacity: 0 })}
      />
      <span aria-hidden="true" className={radioMarkRecipe({ selected })}>
        {selected && (
          <span
            className={css({
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: '{colors.bg.neutral}',
            })}
          />
        )}
      </span>
      <div>
        <div className={css({ marginBottom: '5px' })}>
          <span className={optionTagRecipe({ selected })}>{tag}</span>
        </div>
        <div
          className={css({ fontSize: '14px', fontWeight: 700, color: '{colors.text.neutral}', marginBottom: '4px' })}
        >
          {title}
        </div>
        <div className={css({ fontSize: '13px', color: '{colors.text.subtle}', lineHeight: 1.45 })}>{desc}</div>
        <div
          className={css({
            display: 'flex',
            gap: '6px',
            marginTop: '7px',
            fontSize: '12px',
            color: '{colors.text.subtle}',
            lineHeight: 1.4,
          })}
        >
          <span className={css({ flexShrink: 0 })}>+</span>
          <span>{note}</span>
        </div>
      </div>
    </label>
  );
}

export function SmartAssignActivationModal({
  open,
  onClose,
  inUse,
  total,
}: {
  open: boolean;
  onClose: () => void;
  /** Placements already occupied — these become pinned when Smart Assign turns on. */
  inUse: number;
  /** Total contracted placements. */
  total: number;
}) {
  const { enqueueToast } = useToast();
  const [selected, setSelected] = useState<SmartConfigMode | null>(null);
  const { mutateAsync: saveSmartConfigMode, isPending } =
    useRentalManagerUserPropertySpotlightWebApiHandler_setSmartConfigMode({
      meta: {
        invalidates: ['getProperties', 'spotlightOverview', 'optimizationConfig'],
        context: { api: 'setSmartConfigMode', details: 'turn on smart assign' },
      },
    });

  const fills = Math.max(0, total - inUse);

  const handleClose = () => {
    setSelected(null);
    onClose();
  };

  const handleConfirm = () => {
    if (!selected) {
      return;
    }
    void saveSmartConfigMode({ postData: { autoOptimizeEnabled: true, smartConfigMode: selected } })
      .then(() => {
        enqueueToast(
          <Toast id="smart-assign-saved" tone="success">
            {MODAL_COPY.savedToast}
          </Toast>,
        );
        handleClose();
        return;
      })
      .catch(() => {
        enqueueToast(
          <Toast id="smart-assign-error" tone="critical">
            {MODAL_COPY.errorToast}
          </Toast>,
        );
        return;
      });
  };

  return (
    <Modal.Root open={open} onOpenChange={(next) => !next && handleClose()} size={{ base: 'fullScreen', md: 'sm' }}>
      <Modal.Portal>
        <Modal.Backdrop>
          <Modal.Content data-panda-theme="zillow" style={{ maxWidth: 480, width: 480, paddingBlockStart: 0 }}>
            <div className={css({ padding: '24px 28px 0' })}>
              <h2
                className={css({
                  margin: '0 0 8px',
                  fontSize: '22px',
                  fontWeight: 900,
                  letterSpacing: '-0.01em',
                  color: '{colors.text.neutral}',
                })}
              >
                {MODAL_COPY.title}
              </h2>
              <p
                className={css({
                  margin: '0 0 20px',
                  fontSize: '14px',
                  color: '{colors.text.subtle}',
                  lineHeight: 1.55,
                })}
              >
                {MODAL_COPY.description.lead}
                <strong className={strongNeutral}>{MODAL_COPY.description.bold}</strong>
                {MODAL_COPY.description.tail}
              </p>
              <div
                className={css({
                  display: 'flex',
                  gap: '9px',
                  padding: '11px 13px',
                  marginBottom: '18px',
                  backgroundColor: '{colors.bg.softest}',
                  borderRadius: '10px',
                  fontSize: '12.5px',
                  lineHeight: 1.45,
                  color: '{colors.text.subtle}',
                })}
              >
                <span
                  className={css({
                    flexShrink: 0,
                    marginTop: '1px',
                    color: '{colors.text.feedback.success.hero}',
                    fontWeight: 700,
                  })}
                >
                  ✓
                </span>
                {total === 0 ? (
                  <span>Smart Assign will fill your Spotlight placements as they become available.</span>
                ) : inUse === 0 ? (
                  <span>
                    All <strong className={strongNeutral}>{total}</strong> of your placements are unused, so Smart
                    Assign will fill every one.
                  </span>
                ) : fills === 0 ? (
                  <span>
                    All <strong className={strongNeutral}>{total}</strong> of your placements are in use, so nothing
                    changes right now. Smart Assign will reassign each placement as it frees up.
                  </span>
                ) : (
                  <span>
                    <strong className={strongNeutral}>{inUse}</strong> of your{' '}
                    <strong className={strongNeutral}>{total}</strong> placements are already in use. Those become
                    pinned, and Smart Assign fills the remaining <strong className={strongNeutral}>{fills}</strong>.
                  </span>
                )}
              </div>
              <div
                className={css({
                  fontSize: '12px',
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  color: '{colors.text.subtle}',
                  marginBottom: '10px',
                })}
              >
                {MODAL_COPY.selectLabel}
              </div>
              <Box
                role="radiogroup"
                aria-label={MODAL_COPY.selectLabel}
                css={{ display: 'flex', flexDirection: 'column', gap: '200' }}
              >
                <OptionCard
                  testId="smart-assign-all-included"
                  selected={selected === SmartConfigMode.ALL_INCLUDED}
                  onSelect={() => setSelected(SmartConfigMode.ALL_INCLUDED)}
                  tag={MODAL_COPY.allIncluded.tag}
                  title={MODAL_COPY.allIncluded.title}
                  desc={MODAL_COPY.allIncluded.desc}
                  note={MODAL_COPY.allIncluded.footnote}
                />
                <OptionCard
                  testId="smart-assign-all-excluded"
                  selected={selected === SmartConfigMode.ALL_EXCLUDED}
                  onSelect={() => setSelected(SmartConfigMode.ALL_EXCLUDED)}
                  tag={MODAL_COPY.allExcluded.tag}
                  title={MODAL_COPY.allExcluded.title}
                  desc={MODAL_COPY.allExcluded.desc}
                  note={MODAL_COPY.allExcluded.footnote}
                />
              </Box>
            </div>
            <div className={css({ display: 'flex', justifyContent: 'space-between', padding: '20px 28px 24px' })}>
              <ButtonGroup aria-label="Actions" css={{ width: '100%', justifyContent: 'space-between' }}>
                <TextButton tone="brand" onClick={handleClose}>
                  {MODAL_COPY.cancel}
                </TextButton>
                <Button
                  tone="brand"
                  emphasis="filled"
                  size="md"
                  disabled={!selected || isPending}
                  onClick={handleConfirm}
                >
                  {MODAL_COPY.confirm}
                </Button>
              </ButtonGroup>
            </div>
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal.Root>
  );
}

export default SmartAssignActivationModal;
